/**
 * ProductThumb – universal product thumbnail using media_assets as SoT.
 *
 * Renders:
 * - <img> if src is provided (with onError fallback)
 * - Calm placeholder (first letter monogram) if no image
 *
 * Used across: preorder dialog, product picker, product cards, order rows.
 */

import { useState } from 'react';
import { Package } from 'lucide-react';
import { cn } from '@/lib/utils';

const sizeMap = {
  sm: 'h-10 w-10 rounded-md text-xs',
  md: 'h-12 w-12 rounded-lg text-sm',
  lg: 'h-16 w-16 rounded-xl text-base',
} as const;

export interface ProductThumbProps {
  /** Resolved image URL (prefer path_320 from media_assets) */
  src?: string | null;
  alt?: string | null;
  /** Product title – used for monogram fallback */
  title: string;
  size?: keyof typeof sizeMap;
  className?: string;
}

export function ProductThumb({
  src,
  alt,
  title,
  size = 'md',
  className,
}: ProductThumbProps) {
  const [failed, setFailed] = useState(false);
  const sizeClasses = sizeMap[size];
  const letter = (title?.[0] ?? '?').toUpperCase();

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={alt || title}
        loading="lazy"
        onError={() => setFailed(true)}
        className={cn(sizeClasses, 'object-cover shrink-0', className)}
      />
    );
  }

  return (
    <div
      data-testid="product-thumb-fallback"
      className={cn(
        sizeClasses,
        'bg-muted flex items-center justify-center shrink-0',
        className,
      )}
    >
      <span className="font-medium text-muted-foreground">{letter}</span>
    </div>
  );
}
